/**
 * En qué idioma se dibuja la app. Un store de módulo y no un contexto: las
 * reglas puras de `lib/` (etiquetas de superficie, formato de fechas, textos
 * del cuestionario) también necesitan saberlo, y ahí no hay React.
 *
 * El español es el idioma de la casa y el default: casi todo el que entra
 * busca trabajo desde Colombia. El inglés se elige solo cuando el navegador lo
 * pide PRIMERO, o cuando la persona lo elige en el selector.
 *
 * Orden de decisión: `?lang=` en la URL (enlaces de correos y soporte) → lo que
 * la persona eligió antes → el idioma del navegador → español.
 *
 * La elección se guarda con una clave SIN los prefijos que borra `logout()`:
 * es una preferencia del aparato, no de la sesión. Quien salió en inglés
 * vuelve a entrar en inglés.
 */
export type Locale = 'es' | 'en';

export const LOCALES: Locale[] = ['es', 'en'];

export const DEFAULT_LOCALE: Locale = 'es';

export const LOCALE_STORAGE_KEY = 'ja-locale';

/** Lo que usa `Intl` para fechas y números. Colombia es la casa del español. */
export const INTL_LOCALE: Record<Locale, string> = {
  es: 'es-CO',
  en: 'en-US',
};

export function isLocale(value: unknown): value is Locale {
  return value === 'es' || value === 'en';
}

/** 'en-US' → 'en', 'es-419' → 'es', cualquier otra cosa → null. */
export function normalizeLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const base = value.trim().toLowerCase().split(/[-_]/)[0];
  return isLocale(base) ? base : null;
}

function readStored(): Locale | null {
  try {
    return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    // Safari privado lanza al tocar localStorage: se decide por el navegador.
    return null;
  }
}

function readQuery(): Locale | null {
  try {
    return normalizeLocale(new URLSearchParams(window.location.search).get('lang'));
  } catch {
    return null;
  }
}

/**
 * El primer idioma soportado de la lista del navegador. La lista y no sólo
 * `navigator.language`: un navegador en francés con español de segunda opción
 * debe caer en español, no en el default por accidente.
 */
function readNavigator(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const list = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const candidate of list) {
    const locale = normalizeLocale(candidate);
    if (locale) return locale;
  }
  return null;
}

export function detectLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const fromQuery = readQuery();
  if (fromQuery) {
    persist(fromQuery);
    return fromQuery;
  }
  return readStored() ?? readNavigator() ?? DEFAULT_LOCALE;
}

function persist(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Sin almacenamiento la elección dura lo que dure la pestaña. Suficiente.
  }
}

function applyToDocument(locale: Locale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

let current: Locale = detectLocale();
applyToDocument(current);

const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

/** Cambia el idioma de toda la app. `persist: false` es para las pruebas. */
export function setLocale(locale: Locale, options: { persist?: boolean } = {}): void {
  if (options.persist !== false) persist(locale);
  if (locale === current) return;
  current = locale;
  applyToDocument(locale);
  listeners.forEach((listener) => listener());
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function intlLocale(locale: Locale = current): string {
  return INTL_LOCALE[locale];
}
